import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import { Drawer } from '@openx/components-core/lib/Drawer/Drawer';
import { DrawerActionBar, type DrawerActions } from '@openx/components-core/lib/Drawer/DrawerActionBar';
import { DrawerHeader } from '@openx/components-core/lib/Drawer/DrawerHeader';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InvalidItems } from './InvalidItems/InvalidItems';
import type {
  AppNameSuggestion,
  InvalidAppNameWithSuggestions,
  InvalidItemsError,
  ValidationResult,
  ValidationResultV2,
} from './types';
import { BULK_SPLITER, parseBulkItems } from './utils';

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    background: ${({ theme }) => theme.palette.background.boxLight};
  }

  .MuiPaper-root.MuiAlert-standard {
    width: 100%;
    align-items: center;
    margin-top: ${({ theme }) => theme.spacing(1.5)};
    margin-bottom: ${({ theme }) => theme.spacing(0)};
  }
`;

const StyledInputWrapperGrid = styled(Grid)`
  height: 100%;
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const StyledTextField = styled(TextField)`
  .MuiInput-root {
    height: 100%;
  }

  .MuiInput-root textarea {
    height: 100% !important;
    overflow: auto !important;
  }
`;

interface BulkAddDrawerProps {
  onClose: () => void;
  onApply: (items: string[]) => void;
  errorText?: string;
  children?: ReactNode;
  placeholder: string;
  helperText?: string;
  /** Validation function - can return V2 result with suggestions */
  validate: (value: string[]) => Promise<ValidationResult | ValidationResultV2>;
  limit?: number;
  childParams?: unknown;
  shouldDisableActionButtons?: boolean;
  initialValue?: string;
  /**
   * Custom title for the drawer. Defaults to "Bulk Add".
   */
  title?: string;
  /**
   * V2: Enable suggestions feature for App Name validation.
   * When true, shows suggestions for invalid items that can be added to valid list.
   */
  enableSuggestions?: boolean;
  bulkSplitter?: RegExp;
  disableValueClearing?: boolean;
  shouldDisableAddButton?: boolean;
  // Formats internal item values (e.g. "id:name") to user-facing display strings
  formatForDisplay?: (item: string) => string;
}

export function BulkAddDrawer({
  onClose,
  onApply,
  errorText,
  children,
  placeholder,
  helperText,
  validate,
  limit = Number.MAX_SAFE_INTEGER, // in case limit not provided use max integer as default to not show error
  childParams,
  shouldDisableActionButtons = false,
  initialValue,
  title,
  enableSuggestions = false,
  bulkSplitter,
  disableValueClearing = false,
  shouldDisableAddButton = false,
  formatForDisplay,
}: BulkAddDrawerProps) {
  const [bulkValue, setBulkValue] = useState('');
  const [isAddDisabled, setAddDisabled] = useState(true);
  const [bulkErrors, setBulkErrors] = useState<InvalidItemsError[]>([]);
  const [bulkValidItems, setBulkValidItems] = useState<string[]>([]);
  const [validating, setValidating] = useState(false);
  // V2: Store suggestions for invalid items
  const [invalidItemsWithSuggestions, setInvalidItemsWithSuggestions] = useState<
    InvalidAppNameWithSuggestions[] | undefined
  >(undefined);
  const { t } = useTranslation();

  const parsedItemsCount = useMemo(
    () => parseBulkItems(bulkValue, bulkSplitter ?? BULK_SPLITER).length,
    [bulkValue, bulkSplitter]
  );
  const isInputOverLimit = parsedItemsCount > limit;
  const isLimitReached = bulkValidItems.length > limit || isInputOverLimit;

  // biome-ignore lint/correctness/useExhaustiveDependencies: disable add when params in children changed, as current values needs to be revalidated
  useEffect(() => {
    setAddDisabled(true);
    // V2: Clear suggestions on reset
    setInvalidItemsWithSuggestions(undefined);
    !disableValueClearing && setBulkValue(initialValue ?? '');
  }, [childParams, initialValue]);

  const onValidate = useCallback(async () => {
    const items = parseBulkItems(bulkValue, bulkSplitter ?? BULK_SPLITER);

    // Guard against sending more items than the limit allows. This keeps the request
    // within the backend cap (e.g. appGroupSearch supports at most `limit` names) instead
    // of letting it fail server-side.
    if (items.length > limit) return;

    setValidating(true);

    try {
      const result = await validate(items);
      const { errors, validItems } = result;

      // V2: Store suggestions if available
      const v2Result = result as ValidationResultV2;
      if (enableSuggestions && v2Result.invalidItemsWithSuggestions) {
        setInvalidItemsWithSuggestions(v2Result.invalidItemsWithSuggestions);
      } else {
        setInvalidItemsWithSuggestions(undefined);
      }

      setBulkErrors(errors);
      setBulkValidItems(validItems);

      const displayItems = formatForDisplay ? validItems.map(formatForDisplay) : validItems;
      const validItemsReformated = displayItems.join('\n');
      setBulkValue(validItemsReformated);
      setAddDisabled(!validItemsReformated);
    } catch {
      setInvalidItemsWithSuggestions(undefined);
      setBulkErrors([{ error: t('Validation failed. Please try again.'), invalidItems: [] }]);
      setBulkValidItems([]);
      setAddDisabled(true);
    } finally {
      setValidating(false);
    }
  }, [bulkValue, bulkSplitter, limit, validate, enableSuggestions, formatForDisplay, t]);

  // V2: Handler for adding a suggestion to valid items
  const handleAddSuggestion = useCallback(
    (suggestion: AppNameSuggestion, invalidName: string) => {
      // Add suggestion to valid items using "id:name" format for correct API dispatch
      const newItem = `${suggestion.id}:${suggestion.name}`;
      const newValidItems = [...bulkValidItems, newItem];
      setBulkValidItems(newValidItems);
      const displayItems = formatForDisplay ? newValidItems.map(formatForDisplay) : newValidItems;
      setBulkValue(displayItems.join('\n'));
      setAddDisabled(false);

      // Update suggestions to mark this one as added
      setInvalidItemsWithSuggestions(prev => {
        if (!prev) return prev;
        return prev.map(item => {
          if (item.invalidName === invalidName) {
            return {
              ...item,
              suggestions: item.suggestions.map(s => (s.id === suggestion.id ? { ...s, isAlreadyAdded: true } : s)),
            };
          }
          return item;
        });
      });

      // Update errors count
      setBulkErrors(prev =>
        prev.map(error => {
          if (error.error === 'Invalid App Names') {
            return error; // Keep the same error, just visual feedback changed
          }
          return error;
        })
      );
    },
    [bulkValidItems, formatForDisplay]
  );

  const disableAddButton = isLimitReached || isAddDisabled || shouldDisableActionButtons || shouldDisableAddButton;

  const actions: DrawerActions = useMemo(
    () => [
      {
        color: 'primary',
        disabled: disableAddButton,
        label: t('Add'),
        onClick: () => onApply(bulkValidItems),
      },
      {
        disabled: shouldDisableActionButtons || isInputOverLimit,
        label: t('Validate'),
        loading: validating,
        onClick: onValidate,
      },
      {
        label: t('Cancel'),
        onClick: onClose,
        variant: 'text',
      },
    ],
    [
      t,
      disableAddButton,
      onValidate,
      validating,
      onClose,
      onApply,
      bulkValidItems,
      shouldDisableActionButtons,
      isInputOverLimit,
    ]
  );

  const onChange = useCallback(event => {
    setBulkValue(event.target.value);
    setAddDisabled(true);
  }, []);

  return (
    <StyledDrawer open onClose={onClose} data-test="bulk-side-details" isStackedDrawer>
      <DrawerHeader onClose={onClose} sx={{ mb: 1.25 }} dataTest="bulk-drawer-header">
        {title ?? t('Bulk Add')}
      </DrawerHeader>

      {!!bulkErrors.length && (
        <InvalidItems
          errors={bulkErrors}
          errorText={errorText}
          invalidItemsWithSuggestions={enableSuggestions ? invalidItemsWithSuggestions : undefined}
          onAddSuggestion={enableSuggestions ? handleAddSuggestion : undefined}
        />
      )}

      {children}

      <StyledInputWrapperGrid container>
        <StyledTextField
          dataTest="targeting-bulk-input"
          multiline
          fullWidth
          slotProps={{ input: { disableUnderline: true } }}
          helperText={isLimitReached ? t('The limit has been reached ({limit}).', { limit }) : helperText}
          error={isLimitReached}
          placeholder={placeholder}
          onChange={onChange}
          value={bulkValue}
          disabled={validating}
        />
      </StyledInputWrapperGrid>

      <DrawerActionBar
        actions={actions}
        stickyBottom
        hideDivider
        backgroundColor="inherit"
        dataTest="bulk-drawer-buttons"
      />
    </StyledDrawer>
  );
}
